import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Calendar,
  Plus,
  Pencil,
  Trash2,
  LogOut,
  User,
  Clock,
  MapPin,
  Users,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import ImageSelector from "@/components/admin/ImageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CollaboratorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stages, setStages] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showStageForm, setShowStageForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [form, setForm] = useState({
    title: { fr: "", en: "" },
    summary: { fr: "", en: "" },
    description: { fr: "", en: "" },
    category: "yoga",
    duration_days: 3,
    price: 500,
    max_participants: 12,
    location: "",
    start_date: "",
    end_date: "",
    includes: { fr: [], en: [] },
    image_url: "",
    contact_email: "",
    contact_phone: "",
    website: "",
  });
  const [includesText, setIncludesText] = useState("");

  const token = localStorage.getItem("collaborator_token");

  useEffect(() => {
    if (!token) {
      navigate("/collaborator/login");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [profileRes, stagesRes, statsRes] = await Promise.all([
        axios.get(`${API}/collaborator/profile`, { headers }),
        axios.get(`${API}/collaborator/stages`, { headers }),
        axios.get(`${API}/collaborator/stats`, { headers }),
      ]);
      setUser(profileRes.data);
      setStages(stagesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("collaborator_token");
        navigate("/collaborator/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("collaborator_token");
    navigate("/collaborator/login");
  };

  const resetForm = () => {
    setForm({
      title: { fr: "", en: "" },
      summary: { fr: "", en: "" },
      description: { fr: "", en: "" },
      category: "yoga",
      duration_days: 3,
      price: 500,
      max_participants: 12,
      location: "",
      start_date: "",
      end_date: "",
      includes: { fr: [], en: [] },
      image_url: "",
      contact_email: user?.email || "",
      contact_phone: "",
      website: "",
    });
    setIncludesText("");
    setEditingStage(null);
  };

  const handleEditStage = (stage) => {
    setEditingStage(stage);
    setForm({
      ...stage,
      includes: stage.includes || { fr: [], en: [] },
    });
    setIncludesText(stage.includes?.fr?.join("\n") || "");
    setShowStageForm(true);
  };

  const handleSubmit = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = {
        ...form,
        includes: {
          fr: includesText.split("\n").filter((l) => l.trim()),
          en: includesText.split("\n").filter((l) => l.trim()),
        },
      };

      if (editingStage) {
        await axios.put(
          `${API}/collaborator/stages/${editingStage.id}`,
          formData,
          { headers }
        );
        toast.success("Stage mis à jour avec succès");
      } else {
        await axios.post(`${API}/collaborator/stages`, formData, { headers });
        toast.success("Stage créé avec succès. En attente d'approbation.");
      }

      setShowStageForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving stage:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    }
  };

  const handleDeleteStage = async (stageId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce stage ?")) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/collaborator/stages/${stageId}`, { headers });
      toast.success("Stage supprimé");
      fetchData();
    } catch (error) {
      console.error("Error deleting stage:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const categories = [
    { value: "yoga", label: "Yoga" },
    { value: "meditation", label: "Méditation" },
    { value: "art", label: "Art" },
    { value: "music", label: "Musique" },
    { value: "wellness", label: "Bien-être" },
    { value: "spirituality", label: "Spiritualité" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <User className="h-5 w-5 text-amber-700" />
            </div>
            <div>
              <h1 className="font-semibold text-stone-800">{user?.name}</h1>
              <p className="text-sm text-stone-500">Espace Collaborateur</p>
            </div>
          </div>
          <Button variant="outline" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Stages</p>
                  <p className="text-2xl font-bold">{stats.total_stages || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Publiés</p>
                  <p className="text-2xl font-bold">{stats.published_stages || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">En attente</p>
                  <p className="text-2xl font-bold">{stats.pending_approval || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stages Section */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b flex items-center justify-between">
            <h2 className="text-xl font-semibold">Mes Stages & Retraites</h2>
            <Button
              onClick={() => {
                resetForm();
                setShowStageForm(true);
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Stage
            </Button>
          </div>

          {stages.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500">Vous n'avez pas encore créé de stage</p>
              <Button
                onClick={() => {
                  resetForm();
                  setShowStageForm(true);
                }}
                className="mt-4"
                variant="outline"
              >
                Créer mon premier stage
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {stages.map((stage) => (
                <div key={stage.id} className="p-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {stage.image_url && (
                      <img
                        src={stage.image_url.startsWith("http") ? stage.image_url : `${process.env.REACT_APP_BACKEND_URL}${stage.image_url}`}
                        alt={stage.title?.fr}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{stage.title?.fr}</h3>
                        {stage.is_published ? (
                          <Badge className="bg-green-100 text-green-700">Publié</Badge>
                        ) : stage.is_approved === false ? (
                          <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                        ) : (
                          <Badge variant="secondary">Brouillon</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {stage.duration_days} jours
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {stage.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          Max {stage.max_participants}
                        </span>
                        <span className="font-medium text-amber-600">
                          {stage.price}€
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {stage.is_published && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(`/stages/${stage.slug}`, '_blank')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditStage(stage)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteStage(stage.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Stage Form Dialog */}
      <Dialog open={showStageForm} onOpenChange={setShowStageForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStage ? "Modifier le stage" : "Nouveau stage"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Titre (FR) *</Label>
                <Input
                  value={form.title.fr}
                  onChange={(e) =>
                    setForm({ ...form, title: { ...form.title, fr: e.target.value } })
                  }
                  placeholder="Retraite Yoga Sahara"
                />
              </div>
              <div>
                <Label>Titre (EN)</Label>
                <Input
                  value={form.title.en}
                  onChange={(e) =>
                    setForm({ ...form, title: { ...form.title, en: e.target.value } })
                  }
                  placeholder="Sahara Yoga Retreat"
                />
              </div>
            </div>

            <div>
              <Label>Résumé (FR) *</Label>
              <Textarea
                value={form.summary.fr}
                onChange={(e) =>
                  setForm({ ...form, summary: { ...form.summary, fr: e.target.value } })
                }
                placeholder="Courte description..."
                rows={2}
              />
            </div>

            <div>
              <Label>Description complète (FR) *</Label>
              <Textarea
                value={form.description.fr}
                onChange={(e) =>
                  setForm({
                    ...form,
                    description: { ...form.description, fr: e.target.value },
                  })
                }
                placeholder="Description détaillée du stage..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie *</Label>
                <Select
                  value={form.category}
                  onValueChange={(value) => setForm({ ...form, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Lieu *</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Désert de Chegaga"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Durée (jours) *</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.duration_days}
                  onChange={(e) =>
                    setForm({ ...form, duration_days: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
              <div>
                <Label>Prix (€) *</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) =>
                    setForm({ ...form, price: parseFloat(e.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <Label>Participants max *</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.max_participants}
                  onChange={(e) =>
                    setForm({ ...form, max_participants: parseInt(e.target.value) || 1 })
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            </div>

            <ImageSelector
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
              label="Image du stage"
            />

            <div>
              <Label>Ce qui est inclus (un élément par ligne)</Label>
              <Textarea
                value={includesText}
                onChange={(e) => setIncludesText(e.target.value)}
                placeholder="Hébergement&#10;Repas&#10;Transport&#10;Matériel"
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email de contact *</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="+212 6XX XXX XXX"
                />
              </div>
            </div>

            <div>
              <Label>Site web</Label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageForm(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} className="bg-amber-600 hover:bg-amber-700">
              {editingStage ? "Mettre à jour" : "Créer le stage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollaboratorDashboard;
